// Seeded blog cluster: pillar + cluster model. Real, useful content with internal links.
// block types: {h2}, {p}, {ul:[]}, {cta}

export const CATEGORIES = ["Looksmaxxing", "Skincare", "Facial Fitness", "Guides", "Comparisons"];

export const POSTS = [
  {
    slug: "looksmaxxing-complete-guide",
    title: "Looksmaxxing: The Complete 2026 Guide (Backed by Real Metrics)",
    category: "Looksmaxxing",
    pillar: true,
    date: "2026-01-12",
    author: "Dr. Mara Vance",
    read: 9,
    excerpt:
      "Everything you need to know about looksmaxxing in 2026 — what actually moves the needle, what's a myth, and how to measure your progress objectively.",
    body: [
      { h2: "What looksmaxxing actually means" },
      { p: "Looksmaxxing is the practice of systematically improving your physical appearance through grooming, skincare, fitness, posture and habit changes. Stripped of the internet drama, it's just self-improvement with a feedback loop. The problem most people hit is that they have no objective way to measure whether their changes are working." },
      { p: "That's the gap Aesthetiq fills: an objective, repeatable 100-point analysis across eight facial metrics so you can see what's improving and what isn't, instead of guessing in the mirror." },
      { h2: "The eight metrics that matter" },
      { ul: ["Facial symmetry — balance between the left and right halves", "Jawline definition — sharpness of the jaw and chin", "Facial harmony — how features work together", "Golden ratio alignment", "Facial thirds balance", "Canthal tilt — the angle of your eyes", "Cheekbone prominence", "Skin quality and clarity"] },
      { p: "Some of these are fixed by bone structure. Many — skin quality, jawline definition via body fat, under-eye appearance, even perceived symmetry via posture — respond strongly to effort over weeks and months." },
      { h2: "What actually moves the needle" },
      { ul: ["Skin: a consistent cleanser + sunscreen + retinoid routine is the single highest-leverage change for most people.", "Body fat: lower body fat reveals jaw and cheekbone structure that's already there.", "Sleep & sodium: reducing facial bloat sharpens the lower third within days.", "Grooming: eyebrow shape, hairstyle and beard line frame the whole face.", "Posture & mewing: correct tongue posture and head position improve perceived structure over time."] },
      { h2: "What's mostly a myth" },
      { p: "Bone-smashing and aggressive 'hardmaxxing' trends are unsafe and unsupported. The realistic ceiling for most people — your Future-Face projection in Aesthetiq — comes from optimising the controllable variables above, not from extreme interventions." },
      { h2: "How to track progress the right way" },
      { p: "Scan under consistent lighting, same angle, same time of day, once every 1–2 weeks. Watch the trend, not a single number. Aesthetiq's progress charts (on Pro) make the trend obvious so you can double down on what's working." },
      { cta: "Get your baseline score free" },
    ],
    related: ["jawline-exercises-that-work", "skincare-routine-for-men", "future-face-projection-explained"],
  },
  {
    slug: "jawline-exercises-that-work",
    title: "Jawline Exercises That Actually Work (and the Ones That Don't)",
    category: "Facial Fitness",
    date: "2026-01-18",
    author: "Coach Eli Brooks",
    read: 7,
    excerpt: "A no-nonsense breakdown of what improves jaw definition — from body fat to mewing — and how to measure the change.",
    body: [
      { h2: "Jaw definition is mostly body fat + structure" },
      { p: "The single biggest driver of a visible jawline is body-fat percentage. Below roughly 12–15% for men and 18–22% for women, the existing structure starts to show. No exercise replaces this." },
      { h2: "Mewing: real but slow" },
      { p: "Correct tongue posture (the whole tongue resting on the roof of your mouth) can subtly influence perceived structure and reduces the 'double chin' look from poor posture. It's a long game measured in months, not days." },
      { ul: ["Keep your tongue flat against the palate, not just the tip.", "Lips sealed, teeth lightly touching.", "Hold it as a default resting posture, all day."] },
      { h2: "Reduce the bloat" },
      { p: "Lower sodium, more water, better sleep and less alcohol visibly sharpen the lower third within days. Many people see a 3–6 point jawline metric jump in Aesthetiq from this alone." },
      { h2: "Measure it" },
      { p: "Scan your face, follow the routine for two weeks, scan again under the same lighting. The jawline metric tells you objectively whether it's working." },
      { cta: "Scan your jawline score now" },
    ],
    related: ["looksmaxxing-complete-guide", "reduce-facial-bloat-fast"],
  },
  {
    slug: "skincare-routine-for-men",
    title: "The Minimalist Skincare Routine That Raises Your Skin Score",
    category: "Skincare",
    date: "2026-01-22",
    author: "Dr. Mara Vance",
    read: 6,
    excerpt: "Three products, two minutes a day. The highest-ROI routine for clearer, more even skin — and a higher skin-quality metric.",
    body: [
      { h2: "Why skin is the highest-leverage metric" },
      { p: "Skin quality is one of the few metrics you can dramatically improve fast. Clear, even skin reads as health and youth, and it lifts your overall score more than almost any other change." },
      { h2: "The three-product core" },
      { ul: ["AM: gentle cleanser → broad-spectrum SPF 30+ (non-negotiable).", "PM: gentle cleanser → retinoid (start 2x/week).", "Optional: a simple moisturiser if you get dry."] },
      { p: "That's it. Most 12-step routines are marketing. Consistency beats complexity every time." },
      { h2: "Give it 8–12 weeks" },
      { p: "Skin turnover takes time. Scan at the start, then monthly. The skin-quality metric in Aesthetiq is sensitive enough to pick up texture and evenness changes before they're obvious in the mirror." },
      { cta: "Check your skin-quality score" },
    ],
    related: ["looksmaxxing-complete-guide", "best-ai-face-analysis-tools"],
  },
  {
    slug: "future-face-projection-explained",
    title: "Future-Face Projection: How We Estimate Your Realistic Ceiling",
    category: "Guides",
    date: "2026-01-28",
    author: "The Aesthetiq Team",
    read: 5,
    excerpt: "What your potential score really means, how it's calculated, and how to actually reach it.",
    body: [
      { h2: "Two numbers, not one" },
      { p: "Every Aesthetiq scan gives you an overall score and a potential score. The gap between them is your realistic, controllable upside — the result of optimising skin, body fat, grooming and posture." },
      { h2: "How it's estimated" },
      { p: "The model holds your fixed bone structure constant and projects improvements in the metrics that respond to effort. It's deliberately realistic, not fantasy — the goal is a target you can actually hit." },
      { h2: "Closing the gap" },
      { ul: ["Start with skin and body fat — the biggest, fastest wins.", "Layer in grooming and posture.", "Re-scan every 2 weeks and watch the gap shrink."] },
      { cta: "See your Future-Face projection" },
    ],
    related: ["looksmaxxing-complete-guide", "how-attractive-am-i-test"],
  },
  {
    slug: "best-ai-face-analysis-tools",
    title: "The 10 Best AI Face Analysis Tools in 2026 (Honest Comparison)",
    category: "Comparisons",
    date: "2026-02-02",
    author: "The Aesthetiq Team",
    read: 8,
    excerpt: "We compared every major AI face-rating and looksmaxxing app on accuracy, price, platform and features. Here's the honest rundown.",
    body: [
      { h2: "How we evaluated" },
      { p: "We looked at platform availability, pricing model, depth of analysis, whether there's a real improvement plan, and how easy it is to track progress and share results." },
      { h2: "The shortlist" },
      { ul: ["Aesthetiq — web, 8 metrics, free tier, coaching + projection + leaderboard.", "FaceKit — iOS TrueDepth 3D scan, weekly subscription.", "Umax — mobile, single score, viral.", "LooksMax AI — iOS, broad glow-up coverage.", "Overchat Looksmax — web, style-focused.", "Fotor Pretty Scale — free beauty score.", "PinkMirror — paid web reports.", "PrettyScale — free manual measurement test."] },
      { h2: "The biggest differentiator" },
      { p: "Most tools give you a number and stop. The ones worth using give you a plan and a way to track progress. That's why we built Aesthetiq around coaching and progress, not just scoring." },
      { cta: "Try the #1 web tool free" },
    ],
    related: ["how-attractive-am-i-test", "looksmaxxing-complete-guide"],
  },
  {
    slug: "how-attractive-am-i-test",
    title: "\"How Attractive Am I?\" — How AI Beauty Tests Actually Work",
    category: "Guides",
    date: "2026-02-06",
    author: "Dr. Mara Vance",
    read: 6,
    excerpt: "The science behind AI attractiveness scoring, what's measurable, and how to read your result without spiralling.",
    body: [
      { h2: "What's actually measurable" },
      { p: "AI can reliably estimate geometric proportions: symmetry, ratios, thirds, canthal tilt. These correlate with widely-shared perceptions of attractiveness, but they are not the whole story." },
      { h2: "What a score is and isn't" },
      { p: "Your score is a snapshot of measurable proportions under specific lighting — not a verdict on your worth. Use it as a baseline and a feedback tool, nothing more." },
      { h2: "Read the trend, not the number" },
      { p: "A single scan can swing with lighting and angle. The value is in tracking the trend as you make changes." },
      { cta: "Take the test free" },
    ],
    related: ["future-face-projection-explained", "best-ai-face-analysis-tools"],
  },
  {
    slug: "reduce-facial-bloat-fast",
    title: "How to Reduce Facial Bloat Fast (Before a Photo or Event)",
    category: "Facial Fitness",
    date: "2026-02-10",
    author: "Coach Eli Brooks",
    read: 5,
    excerpt: "Practical, safe ways to de-puff your face in 24–72 hours and sharpen your lower third.",
    body: [
      { h2: "Why your face puffs up" },
      { p: "Facial bloat is mostly water retention driven by sodium, alcohol, poor sleep and stress. The good news: it reverses quickly." },
      { h2: "The 72-hour protocol" },
      { ul: ["Cut added salt and processed food.", "Drink more water, not less.", "Sleep 7–9 hours, head slightly elevated.", "Skip alcohol.", "Cold exposure (cold water splash) in the morning."] },
      { h2: "Measure the difference" },
      { p: "Scan before and after — most people see a visible jawline and harmony bump." },
      { cta: "Scan before your next event" },
    ],
    related: ["jawline-exercises-that-work", "looksmaxxing-complete-guide"],
  },
  {
    slug: "canthal-tilt-explained",
    title: "Canthal Tilt Explained: Why Your Eye Angle Matters",
    category: "Looksmaxxing",
    date: "2026-02-14",
    author: "Dr. Mara Vance",
    read: 5,
    excerpt: "Positive, neutral and negative canthal tilt — what it is, why it's discussed so much, and what you can actually change.",
    body: [
      { h2: "What canthal tilt is" },
      { p: "It's the angle between the inner and outer corners of your eye. A slight upward (positive) tilt is widely perceived as attractive and alert." },
      { h2: "What you can change" },
      { p: "The bony orbit is fixed, but under-eye puffiness, skin laxity and brow position all influence perceived tilt. Sleep, hydration and reducing under-eye bloat help. The canthal-tilt metric in Aesthetiq tracks the perceived angle over time." },
      { cta: "Check your canthal tilt score" },
    ],
    related: ["looksmaxxing-complete-guide", "how-attractive-am-i-test"],
  },
  {
    slug: "facekit-no-iphone",
    title: "No iPhone? Here's How to Get a 3D-Style Face Analysis Anyway",
    category: "Comparisons",
    date: "2026-02-18",
    author: "The Aesthetiq Team",
    read: 5,
    excerpt: "TrueDepth apps lock out Android and desktop users. Here's the web alternative that works on any device.",
    body: [
      { h2: "The TrueDepth lock-out" },
      { p: "Apps like FaceKit rely on the iPhone's TrueDepth sensor, which means Android users, desktop users and older iPhones are left out entirely." },
      { h2: "The web alternative" },
      { p: "Aesthetiq runs a vision-AI analysis from a single photo in your browser — no sensor, no download, no platform lock. You get an 8-metric breakdown, coaching and a shareable card in seconds." },
      { cta: "Analyse your face in the browser" },
    ],
    related: ["best-ai-face-analysis-tools", "looksmaxxing-complete-guide"],
  },
  {
    slug: "facial-symmetry-guide",
    title: "Facial Symmetry: How Much It Matters and How to Improve Yours",
    category: "Looksmaxxing",
    date: "2026-02-22",
    author: "Dr. Mara Vance",
    read: 6,
    excerpt: "Perfect symmetry is a myth — but perceived symmetry is improvable. Here's how.",
    body: [
      { h2: "Nobody is perfectly symmetric" },
      { p: "All faces are asymmetric to some degree, and minor asymmetry is normal and attractive. What you can influence is perceived symmetry." },
      { h2: "Levers you control" },
      { ul: ["Posture and head tilt (huge in photos).", "Chewing evenly on both sides.", "Sleeping position and pillow.", "Reducing one-sided puffiness."] },
      { cta: "Measure your symmetry score" },
    ],
    related: ["jawline-exercises-that-work", "looksmaxxing-complete-guide"],
  },
  {
    slug: "best-photo-for-face-analysis",
    title: "How to Take the Perfect Photo for an Accurate Face Analysis",
    category: "Guides",
    date: "2026-02-26",
    author: "The Aesthetiq Team",
    read: 4,
    excerpt: "Lighting, angle and expression tips so your scan reflects your real proportions.",
    body: [
      { h2: "The five rules" },
      { ul: ["Face the camera straight on, eyes level.", "Soft, even front lighting — avoid harsh overhead light.", "Neutral expression, mouth closed.", "Hair off the face.", "Phone at eye level, not below."] },
      { h2: "Be consistent" },
      { p: "For progress tracking, replicate the same setup each time so changes reflect you, not your lighting." },
      { cta: "Scan with a great photo" },
    ],
    related: ["how-attractive-am-i-test", "looksmaxxing-complete-guide"],
  },
  {
    slug: "looksmaxxing-for-women",
    title: "Looksmaxxing for Women: A Balanced, Evidence-Based Approach",
    category: "Looksmaxxing",
    date: "2026-03-02",
    author: "Dr. Mara Vance",
    read: 7,
    excerpt: "The looksmaxxing conversation is male-dominated, but the highest-leverage habits are universal. Here's a balanced guide.",
    body: [
      { h2: "Same fundamentals, different emphasis" },
      { p: "Skin, sleep, hydration, body composition and grooming drive results for everyone. The framing differs, the biology doesn't." },
      { h2: "High-ROI habits" },
      { ul: ["A simple, consistent skincare routine with daily SPF.", "Prioritising sleep and reducing facial bloat.", "Brow shape and frame.", "Tracking progress objectively instead of comparing to filters."] },
      { cta: "Get your personalised plan" },
    ],
    related: ["skincare-routine-for-men", "looksmaxxing-complete-guide"],
  },
];

export const postBySlug = (slug) => POSTS.find((p) => p.slug === slug);
